import ModalComponent from 'components/main/modal'
import { CLASS_NAME } from 'constants/class-names'
import { EventType, HtmlAttr } from 'constants/enums'
import { ApiService } from 'services/api/api.service'
import { CarComparisonStore } from '../store'
import { ModelCard } from '../model-card/model-card'
import { AbstractCard } from '../abstract-card'
import {
    getResultsUrlPath,
    vehicleIdsInMyPicks,
    vehicleIdsInUrl
} from '../util'
import { logger } from 'utils/system/logger'
import { IDriving, IDrivingVehicle } from '../driving-interface'

const { trimSelector: className, common: commonClass } = CLASS_NAME

/**
 * Trim Selector modal component
 */
export abstract class TrimSelectorBase extends ModalComponent {
    protected trimsWrapper: HTMLDivElement
    protected compareButton: HTMLButtonElement
    protected compareButtonText: HTMLSpanElement
    protected image: HTMLImageElement
    protected make: HTMLHeadingElement
    protected model: HTMLParagraphElement
    protected buttonNoMorePickModal: HTMLButtonElement
    protected buttonNoMorePickModalClose: HTMLButtonElement
    protected myPicksWrapper: HTMLDivElement

    protected checkedItems: number[] = []
    protected updatedItems: string[] = []
    protected vehicleIcons: HTMLDivElement[] = []

    protected url: URL = new URL(window.location.href)
    protected modelCard: ModelCard

    protected get templateName(): string {
        return ''
    }

    /**
     * Get the radio button elements ✅
     *
     * @returns {HTMLInputElement[]}
     */
    protected get radiobuttons(): HTMLInputElement[] {
        return this.getElements('input[type="radio"]', this.modal)
    }

    /**
     * Get the checkbox elements ✅
     *
     * @returns {HTMLInputElement[]}
     */
    protected get checkboxes(): HTMLInputElement[] {
        return this.getElements('input[type="checkbox"]', this.modal)
    }

    public constructor(id: string) {
        super(undefined, id)

        if (!this.bindHTMLElements()) {
            return
        }

        this.bindListeners()
    }

    /**
     * Bind event listeners
     */
    protected bindListeners(): void {
        this.buttonNoMorePickModal.addEventListener(EventType.CLICK, () => {
            CarComparisonStore.getInstance()
                .getNoMorePickModal()
                .closeModal()
            CarComparisonStore.getInstance()
                .getTrimSelectorModel()
                .openModal()
        })

        this.buttonNoMorePickModalClose.addEventListener(
            EventType.CLICK,
            (e) => {
                e.preventDefault()
                CarComparisonStore.getInstance()
                    .getNoMorePickModal()
                    .closeModal()
                CarComparisonStore.getInstance()
                    .getTrimSelectorModel()
                    .openModal()
            }
        )
    }

    /**
     * Bind HTML Elements
     * @returns {boolean} Indicate if the HTML structure is on the screen
     */
    protected bindHTMLElements(): boolean {
        const trimsWrapper = this.getElement<HTMLDivElement>(
            `.${className.wrapper}`,
            this.modal
        )

        const compareButton = this.getElement<HTMLButtonElement>(
            `.${className.button}`,
            this.modal
        )

        const image = this.getElement<HTMLImageElement>(
            `.${className.image}`,
            this.modal
        )

        const make = this.getElement<HTMLHeadingElement>(
            `.${className.make}`,
            this.modal
        )

        const model = this.getElement<HTMLParagraphElement>(
            `.${className.model}`,
            this.modal
        )

        const compareButtonText = this.getElement<HTMLParagraphElement>(
            `.${commonClass.buttonText}`,
            compareButton
        )

        const buttonNoMorePickModal = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.modal._} .${className.buttonNoMorePickModal}`,
            document.body
        )

        const buttonNoMorePickModalClose = this.getElement<HTMLButtonElement>(
            `.${className.noMorePickModal} .${CLASS_NAME.modal.closeBtn}`,
            document.body
        )

        const myPicksWrapper = this.getElement<HTMLDivElement>(
            `.${className.myPicksWrapper}`,
            document.body
        )

        if (
            !trimsWrapper ||
            !compareButton ||
            !image ||
            !make ||
            !model ||
            !compareButtonText ||
            !buttonNoMorePickModal ||
            !buttonNoMorePickModalClose ||
            !myPicksWrapper
        ) {
            return false
        }

        this.trimsWrapper = trimsWrapper
        this.compareButton = compareButton
        this.image = image
        this.make = make
        this.model = model
        this.compareButtonText = compareButtonText
        this.buttonNoMorePickModal = buttonNoMorePickModal
        this.buttonNoMorePickModalClose = buttonNoMorePickModalClose
        this.myPicksWrapper = myPicksWrapper

        this.vehicleIcons = this.getElements(
            `.${className.vehicleIcon}`,
            this.modal
        )
        return true
    }

    /**
     * Open Trim Selector modal
     * Replace the model information and fetch trims from API
     *
     * @param {string} image Image URL
     * @param {number} year
     * @param {string} make
     * @param {string} model
     */
    protected async handleTrimSelector(card: AbstractCard): Promise<void> {
        this.checkedItems = []
        const {
            wrapper,
            year,
            make,
            model,
            vehicleCardID,
            makeName,
            modelName
        } = card

        const cardImage = this.getElement(`.${className.imageModal}`, wrapper)

        // Replace the image
        this.image.src =
            (cardImage && cardImage.getAttribute(HtmlAttr.SOURCE)) || ''

        this.image.alt = `${year} ${make} ${model}`

        // Replace the make
        this.make.innerText = makeName.innerText

        // Replace the model
        this.model.innerText = modelName.innerText

        // Call vehicle API to replace checkboxes
        this.trimsWrapper.innerHTML = 'Loading...'

        await new ApiService()
            .fetchDrivingVehicles<Response>({
                clicked_id: vehicleCardID,
                selected_ids: vehicleIdsInMyPicks(),
                disabled_ids: vehicleIdsInUrl(),
                year: year.toString(),
                make: make,
                model: model,
                format: 'html',
                template_name: this.templateName
            })
            .then(async (data: Response) => this.parseVehiclesCheckboxes(data))
            .catch(() => {
                this.trimsWrapper.innerHTML = ''
            })

        this.setVehicleIcons()
        this.openModal()
    }

    /**
     * Parse vehicle select checkboxes from the response ✅
     *
     * @param {Response} data
     * @returns {Promise<void>}
     */
    protected async parseVehiclesCheckboxes(data: Response): Promise<void> {
        // Replace the checkbox section
        const parser = new DOMParser()
        const text = await data.text()
        const doc = parser.parseFromString(text, 'text/html')

        const body = this.getElement<HTMLBodyElement>('body', doc)
        if (body) {
            this.trimsWrapper.innerHTML = body.innerHTML
        }
        this.handleSelections()
    }

    /**
     * Reload page after update model trim(s)
     * The vehicle datas are required to fetch for price order
     *
     * @param {string} vehicleIds
     */
    protected async reloadPage(vehicleIds: string): Promise<void> {
        await new ApiService()
            .fetchDrivingVehicles<IDriving<IDrivingVehicle>>({
                id: vehicleIds
            })
            .then(async (data: IDriving<IDrivingVehicle>) => {
                this.url.pathname = getResultsUrlPath(data['results'])
            })
            .catch(async (err) => {
                logger('error', `Vehicle API: ${err}`)
                return Promise.reject()
            })
            .then(() => window.location.assign(this.url.toString()))
    }

    /**
     * Handle selector between checkbox and radio
     */
    abstract handleSelections(): void

    /**
     * Assign CSS class to vehicle icons
     */
    abstract setVehicleIcons(): void

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async openTrimSelector(_: AbstractCard): Promise<void> {
        return
    }
}
